import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { Header as ParentHeader } from '@vibesites-weidner-apartment-homes/theme-via6/components/Header';

/**
 * Via-Six property Header.
 *
 * Wraps the Weidner parent Header and DOM-injects a "Penthouse" link
 * under the Features dropdown (desktop + mobile drawer).
 *
 * Why DOM injection instead of pages.json?
 *   Both the base Via6 _HeaderBase and the Weidner Header read their
 *   pages.json files via *static* module imports
 *   (`@vibesites/theme-via6/pages.json` + `../pages.json` relative to
 *   the Weidner theme folder). Neither import follows the property
 *   inheritance chain — so the property-level src/pages.json (where the
 *   new "penthouse" entry lives, with `parent: "features"`) is invisible
 *   to the nav builder. Forking the entire _HeaderBase into the
 *   property just to add one nav row is heavy; the parent Weidner
 *   Header already uses the DOM-injection pattern for the
 *   "Resident Access" → "Residents" rename and the mobile drawer child
 *   marking, so we ride the same trail here.
 *
 * Idempotency:
 *   Each injection is gated by a label / data-attr presence check, so
 *   the MutationObserver re-fires on React reconcile cycles without
 *   churning. When React tears down and re-renders the dropdown
 *   children (hover state flips, mobile drawer open/close, resize
 *   overflow recalc), the observer catches the wipe and re-injects on
 *   the next tick.
 *
 * Tag-along: any anchor we inject into the mobile drawer is marked with
 * the same `data-vibe-nav-child` attribute the parent Header uses to
 * style child rows. The parent's own marker pass only knows about
 * pages declared in the Weidner pages.json — Penthouse isn't there —
 * so we set the flag ourselves to keep the indented child treatment
 * consistent with Amenities / Pet Policy / Future Residents Club.
 */

const PENTHOUSE_LABEL = 'Penthouse';
const PENTHOUSE_HREF = '/penthouse';
const FEATURES_LABEL = 'Features';
// The existing Weidner-defined children of Features. Used to figure
// out where to slot the Penthouse row in the mobile drawer so it sits
// alongside its siblings rather than next to a top-level page.
const FEATURES_CHILD_LABELS = ['Amenities', 'Pet Policy', 'Future Residents Club'];
const NAV_CHILD_FLAG = 'data-vibe-nav-child';
const PENTHOUSE_FLAG = 'data-vibe-penthouse-injected';

/**
 * Inject the Penthouse link into the desktop Features dropdown.
 *
 * `_HeaderBase` renders each parent-with-children nav entry as:
 *
 *   <div class="relative">
 *     <a href="/features" class="...">Features <ChevronDownIcon/></a>
 *     <div class="nav-more-dropdown ...">
 *       <a href="/amenities">Amenities</a>
 *       <a href="/pet-policy">Pet Policy</a>
 *       <a href="/future-residents-club">Future Residents Club</a>
 *     </div>
 *   </div>
 *
 * So we find the "Features" anchor in the main nav, walk up to its
 * wrapper, find the sibling `.nav-more-dropdown`, and append a new
 * <a> at the end if one isn't already there. The dropdown <a>s are
 * always in the DOM (open/closed is opacity-driven), so we don't need
 * to wait for hover to mount the menu.
 */
function injectPenthouseDesktop() {
  const nav = document.querySelector(
    'header.fixed nav[aria-label="Main navigation"]',
  );
  if (!nav) return;

  // The Features anchor — text content is "Features" plus the chevron
  // SVG's empty textContent, so a trim() comparison works.
  const featuresAnchor = Array.from(
    nav.querySelectorAll<HTMLAnchorElement>('a'),
  ).find((a) => a.textContent?.trim() === FEATURES_LABEL);
  if (!featuresAnchor) return;

  const wrapper = featuresAnchor.parentElement;
  if (!wrapper) return;
  const dropdown = wrapper.querySelector<HTMLDivElement>('.nav-more-dropdown');
  if (!dropdown) return;

  // Idempotency — bail if the Penthouse link is already attached to
  // this dropdown (handles React re-renders / observer re-fires
  // without rebuilding the row each time).
  if (dropdown.querySelector(`a[${PENTHOUSE_FLAG}]`)) return;

  // Clone the styling of the existing children so the new link looks
  // identical (same Tailwind classes, same data-* attrs the parent
  // Header CSS keys off of). Falling back to a hand-built <a> if the
  // dropdown is empty for some reason.
  const exemplar = dropdown.querySelector<HTMLAnchorElement>('a');
  const link = exemplar
    ? (exemplar.cloneNode(false) as HTMLAnchorElement)
    : document.createElement('a');
  link.href = PENTHOUSE_HREF;
  link.textContent = PENTHOUSE_LABEL;
  link.setAttribute(PENTHOUSE_FLAG, '');
  dropdown.appendChild(link);
}

/**
 * Inject the Penthouse link into the mobile drawer, indented as a
 * Features child row.
 *
 * Drawer DOM (rendered by _HeaderBase under
 * `header.fixed .mobile-menu-panel nav`):
 *
 *   <a href="/features">Features</a>
 *   <a href="/amenities" data-vibe-nav-child>Amenities</a>
 *   <a href="/pet-policy" data-vibe-nav-child>Pet Policy</a>
 *   <a href="/future-residents-club" data-vibe-nav-child>Future Residents Club</a>
 *   <a href="/gallery-tours">Gallery & Tours</a>
 *   ...
 *
 * We insert the Penthouse row after the last Features child so the
 * grouping stays intact. The Weidner Header's own
 * `markMobileNavChildren()` pass tags rows by label match — Penthouse
 * isn't in its label set (pages.json import doesn't reach this
 * level), so we apply the same `data-vibe-nav-child` flag ourselves.
 */
function injectPenthouseMobile() {
  const drawerNav = document.querySelector(
    'header.fixed .mobile-menu-panel nav',
  );
  if (!drawerNav) return;

  // Idempotency check — same flag as desktop, scoped to drawer anchors.
  const drawerAnchors = Array.from(
    drawerNav.querySelectorAll<HTMLAnchorElement>('a'),
  );
  if (drawerAnchors.some((a) => a.hasAttribute(PENTHOUSE_FLAG))) return;

  const featuresAnchor = drawerAnchors.find(
    (a) => a.textContent?.trim() === FEATURES_LABEL,
  );
  if (!featuresAnchor) return;

  // Pick the last existing Features child row as the insertion anchor.
  // If none of the known children are in the DOM yet (rare reconcile
  // window), fall back to inserting immediately after the Features
  // parent row.
  const lastFeaturesChild = [...drawerAnchors]
    .reverse()
    .find((a) =>
      FEATURES_CHILD_LABELS.includes(a.textContent?.trim() || ''),
    );
  const insertionAnchor = lastFeaturesChild || featuresAnchor;

  // Clone styling from a known Features child when possible (carries
  // the same border / padding / font classes as its siblings); fall
  // back to cloning Features itself if no child anchor is in the DOM.
  const exemplar = lastFeaturesChild || featuresAnchor;
  const link = exemplar.cloneNode(false) as HTMLAnchorElement;
  link.href = PENTHOUSE_HREF;
  link.textContent = PENTHOUSE_LABEL;
  link.setAttribute(NAV_CHILD_FLAG, '');
  link.setAttribute(PENTHOUSE_FLAG, '');

  insertionAnchor.parentNode?.insertBefore(link, insertionAnchor.nextSibling);
}

function runInjections() {
  if (typeof document === 'undefined') return;
  injectPenthouseDesktop();
  injectPenthouseMobile();
}

// Minimal placeholder definition for the hidden schema-lint anchors
// below. The real EditableSlotGroup definitions live in the parent
// _HeaderBase — these stubs only exist so the source-level schema
// linter can statically match each schema key declared by the parent's
// re-attached contentSchema. The wrappers below are never rendered
// visibly (className="hidden" + display:none) and never receive editor
// clicks, so the placeholder definition is harmless.
const lintAnchorDef = { label: '', fields: {} } as unknown as SlotGroupDefinition;

export function Header() {
  useEffect(() => {
    runInjections();
    // The parent Weidner Header already observes document.body for
    // its own DOM rewrites (Residents rename, mobile child marking,
    // logo src restore). Running a second, narrowly-scoped observer
    // here is cheap because each injection is presence-checked — the
    // re-fires are constant-time no-ops once the row is in place.
    const observer = new MutationObserver(runInjections);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <ParentHeader />

      {/* ====================================================================
          Schema-lint anchors — see ../../.claude/skills/build-vibesite/
          reference/shared-component-pattern.md ("Exception to don't hide
          wrappers"). This component is a wrapper that re-attaches the
          parent _HeaderBase's contentSchema; the actual interactive
          EditableToken / EditableSlotGroup wrappers live in
          vendor/.../theme-via6/components/_HeaderBase.tsx. The source-
          level linter checks THIS file for literal `path=` / `groupPath=`
          matches against the re-attached schema's keys, so we add one
          hidden anchor per schema key. Each anchor:

            - Carries the canonical literal path the linter is looking
              for (so wrong-prefix / missing-wrapper rules stay green).
            - Renders nothing visibly (className="hidden" +
              style display:none).
            - Never receives editor clicks — the visible interactive
              wrappers come from the parent _HeaderBase.

          If the parent _HeaderBase's schema gains a new key, add a
          matching anchor here.
          ==================================================================== */}
      <EditableToken
        path="header.phone"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableSlotGroup
        groupPath="header.logo"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.apply_now_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.resident_access_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.qualifying_criteria_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.schedule_tour_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.address_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="header.view_floor_plans_link"
        definition={lintAnchorDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

// Forward the parent Header's contentSchema so the editor sees the
// same set of editable header slots (logo, utility links, CTA, etc.)
// when this wrapper sits in the registry's Header slot. Without this,
// the editor would render an empty inspector panel for the header.
const parentSchema = (ParentHeader as unknown as { contentSchema: ComponentContentSchema })
  .contentSchema;
(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  parentSchema;

export default Header;
